import * as THREE from 'three';
import { clamp, type TeamId, type Vec3 } from '@game/shared';
import {
  TEAM_COLORS,
  applyAirPose,
  applyCrouchPose,
  applySlidePose,
  buildCharacterRig,
  disposeCharacterRig,
  type CharacterRig,
} from './players.js';

/**
 * The local player's own third-person body, shown only in the walkable lobby
 * where the camera orbits it — the place you actually get to look at the
 * character (and later the skins) you picked. Built from the same rig as every
 * remote player, so what you see is exactly what everyone else sees.
 *
 * Driven from the predicted player state each frame; remote interpolation never
 * touches it.
 */
export class LocalAvatar {
  private rig: CharacterRig | null = null;
  private character = -1;
  private team: TeamId = 0;
  private walkPhase = 0;
  private crouchAmount = 0;
  private slideAmount = 0;
  private airAmount = 0;
  private landDip = 0;
  private prevGrounded = true;
  private lastX = 0;
  private lastZ = 0;

  constructor(private readonly scene: THREE.Scene) {}

  /** Rebuilds the rig lazily when the character or team changes. */
  setAppearance(character: number, team: TeamId): void {
    if (this.rig && character === this.character && team === this.team) return;
    this.destroy();
    this.character = character;
    this.team = team;
    this.rig = buildCharacterRig(character, TEAM_COLORS[team]);
    this.rig.group.visible = false;
    this.scene.add(this.rig.group);
  }

  setVisible(visible: boolean): void {
    if (this.rig) this.rig.group.visible = visible;
  }

  update(
    dt: number,
    position: Vec3,
    yaw: number,
    crouching: boolean,
    sliding: boolean,
    onGround: boolean,
    verticalSpeed: number,
  ): void {
    const rig = this.rig;
    if (!rig || !rig.group.visible) return;

    rig.group.position.set(position.x, position.y, position.z);
    rig.group.rotation.y = yaw;

    // Same distance-driven gait as the remote renderer, fed from prediction.
    const moved = Math.hypot(position.x - this.lastX, position.z - this.lastZ);
    this.lastX = position.x;
    this.lastZ = position.z;
    this.walkPhase += moved * 5.5;
    const swing = Math.min(0.9, moved * 60) * Math.sin(this.walkPhase);

    // Same eased kneel, slide and jump tuck as remote players, fed from
    // prediction. The pose layers own the arms.
    if (onGround && !this.prevGrounded && this.airAmount > 0.25) this.landDip = 0.45;
    this.prevGrounded = onGround;
    this.airAmount = clamp(this.airAmount + (onGround ? -dt : dt) * 11, 0, 1);
    this.landDip *= Math.exp(-9 * dt);
    this.crouchAmount = clamp(this.crouchAmount + ((crouching || sliding) ? dt : -dt) * 8, 0, 1);
    this.slideAmount = clamp(this.slideAmount + (sliding ? dt : -dt) * 12, 0, 1);
    applyCrouchPose(rig, Math.min(1, this.crouchAmount + this.landDip), swing);
    applyAirPose(rig, this.airAmount, clamp(0.5 - verticalSpeed / 16, 0, 1));
    applySlidePose(rig, this.slideAmount);
  }

  private destroy(): void {
    if (!this.rig) return;
    this.scene.remove(this.rig.group);
    disposeCharacterRig(this.rig);
    this.rig = null;
  }

  dispose(): void {
    this.destroy();
    this.character = -1;
  }
}
